import React, { useState } from 'react';

const PRESET_AMOUNTS = [100, 250, 500, 1000];

/** Payfast rejects anything smaller; public/api/payfast.php enforces it too. */
const MIN_AMOUNT = 5;

type Frequency = 'once' | 'monthly';

/**
 * Donation widget for the Support Us section.
 *
 * This is a plain HTML form POST rather than a fetch, which means the payment
 * path still works if the React bundle fails to load, and means the amount is
 * validated server-side in public/api/payfast.php. The checks here are a
 * convenience for the donor, not a security boundary.
 */
const DonateForm: React.FC = () => {
    const [selected, setSelected] = useState<number | 'other'>(250);
    const [otherAmount, setOtherAmount] = useState('');
    const [frequency, setFrequency] = useState<Frequency>('once');

    const amount = selected === 'other' ? Number(otherAmount) : selected;
    const isValid = Number.isFinite(amount) && amount >= MIN_AMOUNT;

    return (
        <form className="donate-form" method="POST" action="/api/payfast.php">
            <div className="donate-field">
                <span className="donate-label">I would like to give</span>
                <div className="donate-frequency">
                    <button
                        type="button"
                        className={frequency === 'once' ? 'active' : ''}
                        aria-pressed={frequency === 'once'}
                        onClick={() => setFrequency('once')}
                    >
                        Once-off
                    </button>
                    <button
                        type="button"
                        className={frequency === 'monthly' ? 'active' : ''}
                        aria-pressed={frequency === 'monthly'}
                        onClick={() => setFrequency('monthly')}
                    >
                        Monthly
                    </button>
                </div>
            </div>

            <div className="donate-field">
                <span className="donate-label">Amount</span>
                <div className="donate-chips">
                    {PRESET_AMOUNTS.map((value) => (
                        <button
                            key={value}
                            type="button"
                            className={`donate-chip${selected === value ? ' selected' : ''}`}
                            aria-pressed={selected === value}
                            onClick={() => setSelected(value)}
                        >
                            R{value}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`donate-chip${selected === 'other' ? ' selected' : ''}`}
                        aria-pressed={selected === 'other'}
                        onClick={() => setSelected('other')}
                    >
                        Other
                    </button>
                </div>

                {selected === 'other' && (
                    <div className="donate-other">
                        <span className="donate-other-prefix">R</span>
                        <input
                            type="number"
                            className="donate-other-input"
                            value={otherAmount}
                            onChange={(e) => setOtherAmount(e.target.value)}
                            min={MIN_AMOUNT}
                            step="1"
                            inputMode="numeric"
                            placeholder="Enter an amount"
                            aria-label="Donation amount in rand"
                        />
                    </div>
                )}
            </div>

            <input type="hidden" name="amount" value={isValid ? amount.toFixed(2) : ''} />
            <input type="hidden" name="frequency" value={frequency} />

            <button type="submit" className="donate-button" disabled={!isValid}>
                {isValid
                    ? `Donate R${amount.toLocaleString('en-ZA')}${frequency === 'monthly' ? ' / month' : ''}`
                    : `Enter at least R${MIN_AMOUNT}`}
            </button>

            <p className="donate-form-note">
                Secure payment handled by Payfast — card, Instant EFT, SnapScan and more.
                {frequency === 'monthly'
                    ? ' Monthly giving uses a credit card and you can cancel at any time.'
                    : ''}
            </p>
        </form>
    );
};

export default DonateForm;
